// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { COUNTRIES } from '../data/countries.js';
import '../styles/residency-modal.css';

/** The attestation sentence, built from the picked country's display name.
 *  Exported and test-pinned: it is the exact wording the user is asserting,
 *  so a reworded "improvement" should fail a test and be re-ratified rather
 *  than ship. */
export function residencyStatement(countryName: string): string {
  return `I confirm that I am a resident of ${countryName}.`;
}

interface ResidencyModalProps {
  /** Pre-select this ISO-3166-1 alpha-2 code, when the user has attested
   *  before (re-opening to correct or update it). */
  initialJurisdiction?: string | null;
  /** The user dismissed the prompt — "Not now", ×, overlay, or Esc. */
  onDismiss: () => void;
  /** productco-kyc accepted the attestation. Carries the jurisdiction IT
   *  echoed back, not the one the client sent. */
  onAttested: (jurisdiction: string) => void;
}

/**
 * COB-549 (GF-4) — the residency attestation modal.
 *
 * Collects one fact: which country the user says they live in. It is sent to
 * `POST /auth/residency/attest`, which records it in productco-kyc's
 * append-only store (authoritative) and mirrors it into user-app's local UX
 * copy. The offer gate then has something to decide from.
 *
 * **Dismissible by design, and that is load-bearing.** The prompt is not the
 * enforcement point — the gate at productco-kyc/trading is — so a modal that
 * could not be dismissed would buy no compliance and would put a wall
 * between an authenticated user and their own vaults. Someone who says "Not
 * now" keeps every servicing surface (view, sign, withdraw); the only
 * consequence is that the gate has less to go on and may decline to show an
 * offer. A failed submission is likewise non-blocking: the error is inline,
 * the retry is a click, and Not now still works.
 *
 * Mirrors `terms-modal.tsx`'s chrome (overlay dialog, ×, Esc to close,
 * inline submit error) so the two prompts a user may meet on Home read as
 * one family.
 */
export function ResidencyModal({
  initialJurisdiction,
  onDismiss,
  onAttested,
}: ResidencyModalProps) {
  const [selected, setSelected] = useState<string>(initialJurisdiction ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Esc dismisses, exactly like the terms modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const country = COUNTRIES.find((c) => c.code === selected) ?? null;

  async function handleConfirm() {
    if (!country) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await api.attestResidency(country.code);
      // Record what productco-kyc says it stored — the authoritative answer.
      onAttested(res.jurisdiction);
    } catch {
      // Deliberately one message for every failure kind: the user cannot act
      // differently on a 502 than on a 500, and naming the upstream would
      // leak topology for no benefit. Still dismissible underneath.
      setSubmitError("We couldn't record that just now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      class="residency-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="residency-modal-title"
      onClick={onDismiss}
    >
      <div class="residency-modal-card" onClick={(e) => e.stopPropagation()}>
        <div class="residency-modal-head">
          <h2 id="residency-modal-title">Confirm your country of residence</h2>
          <button
            type="button"
            class="residency-modal-close"
            aria-label="Close"
            onClick={onDismiss}
          >
            ×
          </button>
        </div>
        <p class="residency-modal-intro">
          Cobault offers its yield strategies in some countries and not
          others. Telling us where you live lets us show you what's available
          to you. Your existing vaults are unaffected either way.
        </p>
        <label class="residency-modal-label" for="residency-country">
          Country of residence
        </label>
        <select
          id="residency-country"
          class="residency-modal-select"
          value={selected}
          disabled={submitting}
          onChange={(e) => setSelected((e.target as HTMLSelectElement).value)}
        >
          <option value="">Select a country…</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        {country && (
          <p class="residency-modal-statement">
            {residencyStatement(country.name)}
          </p>
        )}
        {submitError && <p class="residency-modal-error">{submitError}</p>}
        <div class="residency-modal-actions">
          <button
            type="button"
            class="btn btn-secondary"
            onClick={onDismiss}
            disabled={submitting}
          >
            Not now
          </button>
          <button
            type="button"
            class="btn btn-primary"
            onClick={handleConfirm}
            disabled={submitting || !country}
          >
            {submitting ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
